import type { DateRange } from "react-day-picker";
import { Skeleton } from "@/components/ui/skeleton";
import {
  XAxis,
  YAxis,
  Area,
  ComposedChart,
  CartesianGrid,
  Line,
} from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import type { Asset, BenchmarkPoint } from "@/schemas";
import { BenchmarkSelector } from "../BenchmarkSelector";
import { TWRRangePicker } from "./TWRRangePicker";
import {
  formatChartTooltipLabel,
  formatPercent,
} from "../../utils/format-utils";
import { CHART_ANIMATION } from "@/lib/chart-animation";
import { cn } from "@/lib/utils";

const PERIODS = ["1d", "1w", "1m", "1y", "max"];

interface TWRChartSectionProps {
  // Chart data
  mergedChartData: { timestamp: string; twr: number; benchmark?: number }[];
  yAxisDomain: [number, number];
  formatTwrXAxis: (timestamp: string) => string;
  loading: boolean;
  // TWR metrics
  lastTwr: number;
  mode: string;
  annualizedReturn: number | null;
  // Period selector
  period?: string;
  onPeriodChange?: (period: string) => void;
  // Range picker
  rangeEnabled: boolean;
  range: DateRange | undefined;
  formattedRange: string;
  rangePopoverOpen: boolean;
  onRangePopoverOpenChange: (open: boolean) => void;
  onRangeEnableChange: (enabled: boolean) => void;
  onRangeChange: (range: DateRange | undefined) => void;
  // Benchmark
  benchmarkEnabled: boolean;
  onBenchmarkEnabledChange: (enabled: boolean) => void;
  benchmarkAsset: Asset | null;
  onBenchmarkAssetChange: (asset: Asset | null) => void;
  benchmarkData: BenchmarkPoint[] | null;
  benchmarkLoading: boolean;
  lastBenchmarkReturn: number | null;
}

const twrChartConfig = {
  twr: {
    label: "TWR",
    color: "hsl(var(--chart-2, var(--primary)))",
  },
  benchmark: {
    label: "Benchmark",
    color: "hsl(25, 95%, 53%)", // Orange color for benchmark
  },
} satisfies ChartConfig;

export function TWRChartSection({
  mergedChartData,
  yAxisDomain,
  formatTwrXAxis,
  loading,
  lastTwr,
  mode,
  period,
  onPeriodChange,
  rangeEnabled,
  range,
  formattedRange,
  rangePopoverOpen,
  onRangePopoverOpenChange,
  onRangeEnableChange,
  onRangeChange,
  benchmarkEnabled,
  onBenchmarkEnabledChange,
  benchmarkAsset,
  onBenchmarkAssetChange,
  benchmarkData,
  benchmarkLoading,
  lastBenchmarkReturn,
}: TWRChartSectionProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Header with benchmark selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h3 className="text-xl font-bold tracking-tight">
            Time-Weighted Return
          </h3>
          <p className="text-sm text-muted-foreground font-medium">
            Growth relative to cash flow events
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period Selector */}
          {onPeriodChange && period && (
            <div className="inline-flex w-fit max-w-full items-center gap-1 bg-secondary/50 rounded-full p-1 border border-border/40 overflow-x-auto no-scrollbar">
              {PERIODS.map((p) => (
                <button
                  key={p}
                  onClick={() => onPeriodChange(p)}
                  className={cn(
                    "min-w-[2.25rem] px-2 sm:px-3 py-1 text-[10px] font-semibold rounded-full transition-all",
                    period === p
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          <TWRRangePicker
            enabled={rangeEnabled}
            range={range}
            formattedRange={formattedRange}
            popoverOpen={rangePopoverOpen}
            onPopoverOpenChange={onRangePopoverOpenChange}
            onEnableChange={onRangeEnableChange}
            onRangeChange={onRangeChange}
          />

          <BenchmarkSelector
            enabled={benchmarkEnabled}
            onEnabledChange={onBenchmarkEnabledChange}
            selectedAsset={benchmarkAsset}
            onAssetSelect={onBenchmarkAssetChange}
            loading={benchmarkLoading}
          />
        </div>
      </div>

      {/* Performance comparison cards */}
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 flex-wrap">
        {/* Portfolio TWR */}
        <div className="bg-primary/5 rounded-xl p-2 px-3 border border-primary/10">
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] font-bold text-primary uppercase tracking-widest">
              Portfolio
            </span>
            <span className="text-base sm:text-xl font-bold tracking-tight text-primary">
              {formatPercent(lastTwr)}
            </span>
          </div>
        </div>

        {/* Benchmark comparison */}
        {benchmarkEnabled &&
          benchmarkAsset &&
          benchmarkData &&
          benchmarkData.length > 0 &&
          lastBenchmarkReturn !== null && (
            <div className="bg-orange-500/5 rounded-xl p-2 px-3 border border-orange-500/10">
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] font-bold text-orange-500 uppercase tracking-widest">
                  {benchmarkAsset.symbol}
                </span>
                <span className="text-base sm:text-xl font-bold tracking-tight text-orange-500">
                  {formatPercent(lastBenchmarkReturn)}
                </span>
              </div>
            </div>
          )}

        <div className="ml-auto">
          <span className="px-2 py-1 rounded-full bg-secondary text-[9px] font-bold text-muted-foreground uppercase tracking-widest">
            {mode || "advanced"}
          </span>
        </div>
      </div>

      <div className="min-h-0 pb-3 h-[220px] sm:h-[300px] md:h-[350px] lg:h-[450px]">
        {loading ? (
          <Skeleton className="h-full w-full rounded-xl" />
        ) : mergedChartData.length > 0 ? (
          <ChartContainer
            config={twrChartConfig}
            className="h-full w-full min-h-[220px]"
          >
            <ComposedChart
              data={mergedChartData}
              margin={{ top: 10, left: 10, right: 20, bottom: 16 }}
            >
              <defs>
                <linearGradient id="colorTWR" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="hsl(var(--chart-2))"
                    stopOpacity={0.15}
                  />
                  <stop
                    offset="95%"
                    stopColor="hsl(var(--chart-2))"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                opacity={0.3}
                vertical={false}
              />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatTwrXAxis}
                tick={{
                  fontSize: 10,
                  fill: "hsl(var(--muted-foreground))",
                }}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis
                domain={yAxisDomain}
                tickFormatter={(value) => `${value.toFixed(1)}%`}
                tick={{
                  fontSize: 10,
                  fill: "hsl(var(--muted-foreground))",
                }}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={55}
              />
              <ChartTooltip
                cursor={{
                  stroke: "hsl(var(--muted-foreground))",
                  strokeWidth: 1,
                  strokeOpacity: 0.2,
                }}
                content={
                    <ChartTooltipContent
                    labelFormatter={(value) =>
                      formatChartTooltipLabel(
                        String(value),
                        rangeEnabled ? "max" : (period ?? "max"),
                      )
                    }
                  />
                }
              />
              {/* TWR Area */}
              <Area
                type="monotone"
                dataKey="twr"
                name="Portfolio"
                stroke="hsl(var(--chart-2))"
                strokeWidth={2}
                fill="url(#colorTWR)"
                dot={false}
                {...CHART_ANIMATION}
              />
              {/* Benchmark Line (solid with opacity) */}
              {benchmarkEnabled &&
                benchmarkData &&
                benchmarkData.length > 0 && (
                  <Line
                    type="monotone"
                    dataKey="benchmark"
                    name={benchmarkAsset?.symbol || "Benchmark"}
                    stroke="rgb(249, 115, 22)"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    {...CHART_ANIMATION}
                  />
                )}
            </ComposedChart>
          </ChartContainer>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm font-medium">
            No TWR data available
          </div>
        )}
      </div>
    </div>
  );
}
